import * as Popover from "@radix-ui/react-popover";
import { mergeAttributes, Node } from "@tiptap/core";
import { ReactRenderer } from "@tiptap/react";
import Suggestion, {
  SuggestionKeyDownProps,
  SuggestionProps,
} from "@tiptap/suggestion";
import { forwardRef, useImperativeHandle, useRef } from "react";
import { Command, CommandItem, CommandList } from "src/components/ui/command";
import tippy, { Instance, Props as TippyProps } from "tippy.js";
import CommandTunnelOutlet from "./command";

export type VariableOptions = {
  variables: string[];
};

export const VariableNode = Node.create<VariableOptions>({
  name: "variable",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addOptions() {
    return {
      variables: [],
    };
  },
  addAttributes() {
    return {
      name: {
        default: "",
        parseHTML: (el) => (el as HTMLElement).dataset.name,
        renderHTML: (attrs) => ({ "data-name": attrs.name }),
      },
    };
  },
  parseHTML() {
    return [{ tag: "span[data-variable]" }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        "data-variable": "",
        class: "variable-pill",
      }),
      `{{${HTMLAttributes["data-name"]}}}`,
    ];
  },

  addNodeView() {
    return ({ node }) => {
      const span = document.createElement("span");
  
      span.className =
        "variable-pill inline-flex items-center rounded " +
        "bg-gray-100 px-1 text-sm text-primary align-baseline";
      span.textContent = `{{${node.attrs.name}}}`;
  
      /* 👇 zero-width space prevents big caret */
      span.append("\u200B");            // <- ZWSP character
  
      return { dom: span };
    };
  },

  addCommands() {
    return {
      insertVariable:
        (name) =>
        ({ commands }) =>
          commands.insertContent({
            type: this.name,
            attrs: { name },
          }),
    };
  },

  addProseMirrorPlugins() {
    const { variables } = this.options;

    return [
      Suggestion({
        editor: this.editor,
        char: "{{",
        allowSpaces: false,

        items: ({ query }) =>
          variables.filter((v) => v.toLowerCase().includes(query.toLowerCase())),

        // command: ({ editor, range, props }) => {
        //   // Abort if we don’t actually have “{{”
        //   const prev = editor.state.doc.textBetween(range.from - 1, range.from);
        //   console.log(prev);
        //   if (prev !== "{") return false;
        //   editor
        //     .chain()
        //     .deleteRange({ from: range.from - 2, to: range.to })
        //     .insertVariable(props)
        //     .run();
        //   return true;
        // },
        command: ({ editor, range, props }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertVariable(props)
            .run();
        },

        render: () => {
          let component: ReactRenderer | null = null;
  let popup: Instance<TippyProps>[] | null = null;

  return {
    onStart: (props) => {
      const { editor } = props;

      // block code blocks same as slash-extension
      const { selection } = editor.state;
      const parentNode = selection.$from.node(selection.$from.depth);
      if (parentNode.type.name === "codeBlock") return false;

      component = new ReactRenderer(CommandTunnelOutlet, {
        editor,
        props,
        as: "div",
      });

      // @ts-expect-error – tippy types expect strict function
      popup = tippy("body", {
        getReferenceClientRect: props.clientRect,
        appendTo: () => document.body,
        content: component.element,
        showOnCreate: true,
        interactive: true,
        trigger: "manual",
        placement: "bottom-start",
        id: "variable-extension-dom-id",
      });
    },

    onUpdate: (props) => {
      component?.updateProps(props);
      popup?.[0]?.setProps({
        // @ts-ignore
        getReferenceClientRect: props.clientRect,
      });
    },

    onKeyDown: (props) => {
      if (props.event.key === "Escape") {
        popup?.[0]?.hide();
        return true;
      }
      // delegate arrows / enter to cmd‑k list
      // @ts-ignore
      return component?.ref?.onKeyDown(props) ?? false;
    },

    onExit: () => {
      popup?.[0]?.destroy();
      component?.destroy();
    },
  };
        },
      }),
    ];
  },
});